import { z } from 'zod'

import {
  type DefinitionAt,
  type HttpMethod,
  interpolatePath,
  type PathsFor,
  type RouteError,
  type RouteInputArgs,
  type RouteRegistry,
  type RouteResponse,
} from './route-contract'

const apiErrorSchema = z.looseObject({ name: z.string(), message: z.string() })

/** Errors raised by the client itself rather than sent by the server. */
type ClientError = { name: 'NetworkError' | 'UnknownError'; message: string }

export type RouteResult<T, E> =
  | { success: true; data: T }
  | { success: false; error: E | ClientError }

/**
 * Returns a result's `data` on success, or throws an `Error` whose `name` preserves
 * the server-provided error name (e.g. `'ConflictError'`).
 */
export function dataOrThrow<T, E extends { name: string; message: string }>(
  result: RouteResult<T, E>
): T {
  if (result.success) return result.data
  const error = new Error(result.error.message)
  error.name = result.error.name
  throw error
}

type RouteClientMethod<R extends RouteRegistry, M extends HttpMethod> = <P extends PathsFor<R, M>>(
  path: P,
  ...input: RouteInputArgs<DefinitionAt<R, P, M>>
) => Promise<RouteResult<RouteResponse<DefinitionAt<R, P, M>>, RouteError<DefinitionAt<R, P, M>>>>

export type RouteClient<R extends RouteRegistry> = {
  get: RouteClientMethod<R, 'GET'>
  post: RouteClientMethod<R, 'POST'>
  put: RouteClientMethod<R, 'PUT'>
  patch: RouteClientMethod<R, 'PATCH'>
  delete: RouteClientMethod<R, 'DELETE'>
}

export interface RouteClientOptions {
  baseUrl: string
  /** Extra headers resolved per request, e.g. a fresh bearer token. */
  headers?: () => Promise<Record<string, string>>
  credentials?: RequestCredentials
}

type RequestInput = {
  params?: Record<string, unknown>
  query?: Record<string, unknown>
  body?: unknown
}

/**
 * A client typed by the registry type `R`; it never needs the registry itself, so no schemas are bundled.
 * Errors are typed per route and discriminated by `name`.
 */
export function createRouteClient<R extends RouteRegistry>(
  options: RouteClientOptions
): RouteClient<R> {
  async function request(method: HttpMethod, path: string, input: RequestInput = {}) {
    // A missing param is a bug at the call site, so it throws instead of becoming an error response.
    const url = options.baseUrl + interpolatePath(path, input.params) + toQueryString(input.query)
    const headers = new Headers({
      'Content-Type': 'application/json',
      ...(await options.headers?.()),
    })

    let response: Response
    let data: unknown
    try {
      response = await fetch(
        new Request(url, {
          method,
          body: input.body === undefined ? undefined : JSON.stringify(input.body),
          headers,
          credentials: options.credentials,
        })
      )
      if (response.ok) data = await readBody(response)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error'
      return { success: false, error: { name: 'NetworkError', message } }
    }

    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(await response.json().catch(() => null))
      return {
        success: false,
        error: parsed.success ? parsed.data : { name: 'UnknownError', message: 'Unknown error' },
      }
    }
    return { success: true, data }
  }

  const method = (m: HttpMethod) => (path: string, input?: RequestInput) => request(m, path, input)

  return {
    get: method('GET'),
    post: method('POST'),
    put: method('PUT'),
    patch: method('PATCH'),
    delete: method('DELETE'),
  } as RouteClient<R>
}

/** JSON for data routes, nothing for 204, and a `File` named by `Content-Disposition` for file routes. */
async function readBody(response: Response) {
  const contentType = response.headers.get('Content-Type') ?? ''
  if (contentType.startsWith('application/json')) return response.json()
  if (response.status === 204) return undefined

  const encodedName = response.headers
    .get('Content-Disposition')
    ?.match(/filename\*=UTF-8''([^;]+)/)?.[1]
  const fileName = encodedName ? decodeURIComponent(encodedName) : 'download'
  return new File([await response.blob()], fileName, { type: contentType })
}

/** Repeats the key for each array item (`status=open&status=done`), as the router reads arrays. */
function toQueryString(query: Record<string, unknown> | undefined) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(query ?? {})) {
    for (const item of [value].flat()) {
      if (item == null) continue
      search.append(key, item instanceof Date ? item.toISOString() : String(item))
    }
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}
